/**
 * FINN.AI - Global Theme Object
 *
 * Single source of truth for all design tokens.
 * Consumed by:
 *   - ThemeProvider  → injects CSS custom properties on <html>
 *   - Components     → import `theme` for inline style tokens
 *   - global.css     → CSS vars declared here are the static fallback
 *
 * IMPORTANT: This app is dark-only. There is no light mode.
 * Background spec:
 *   linear-gradient(180deg, #010204 0%, #000101 100%),
 *   radial-gradient(36.46% 41.67% at 100% 100%, rgba(0,19,64,0.25) 0%, rgba(0,19,64,0) 60%)
 */

import { colors } from './colors';
import { typography } from './typography';
import { spacing, layout } from './spacing';
import { breakpoints, mediaQueries } from './breakpoints';

export const theme = {
  colors,
  typography,
  spacing,
  layout,
  breakpoints,
  mediaQueries,

  /**
   * Border-radius tokens
   * Sharp/terminal aesthetic - near-flat with only xs/sm rounding.
   */
  borderRadius: {
    none: '0px',
    xs: '2px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    '2xl': '16px',
    full: '9999px',
  },

  /** Shadow / glow tokens */
  shadows: {
    sm: '0 1px 4px rgba(0,0,0,0.6)',
    md: '0 4px 16px rgba(0,0,0,0.7)',
    lg: '0 8px 32px rgba(0,0,0,0.8)',
    xl: '0 16px 64px rgba(0,0,0,0.9)',
    glowTeal: '0 0 20px rgba(0,207,207,0.35), 0 0 60px rgba(0,207,207,0.12)',
    glowAmber: '0 0 20px rgba(255,193,7,0.35),  0 0 60px rgba(255,193,7,0.12)',
  },

  /** Transition presets */
  transitions: {
    fast: 'all 0.15s ease',
    base: 'all 0.25s ease',
    slow: 'all 0.4s ease',
    spring: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },

  /** Z-index scale */
  zIndex: {
    base: 0,
    raised: 10,
    dropdown: 100,
    sticky: 200,
    overlay: 300,
    modal: 400,
    popover: 500,
    toast: 600,
    tooltip: 700,
  },
} as const;

export type Theme = typeof theme;

// ─────────────────────────────────────────────────────────────────────────────
// CSS Custom Properties map
// ThemeProvider injects these onto <html> at runtime.
// global.css mirrors them as a static fallback (FOUC protection).
// ─────────────────────────────────────────────────────────────────────────────
export const cssVariables: Record<string, string> = {
  // ── Teal primary ──────────────────────────────────────────────
  '--color-primary-50': colors.primary[50],
  '--color-primary-100': colors.primary[100],
  '--color-primary-200': colors.primary[200],
  '--color-primary-300': colors.primary[300],
  '--color-primary-400': colors.primary[400],
  '--color-primary-500': colors.primary[500],
  '--color-primary-600': colors.primary[600],
  '--color-primary-700': colors.primary[700],
  '--color-primary-800': colors.primary[800],
  '--color-primary-900': colors.primary[900],

  // ── Dark base surfaces ────────────────────────────────────────
  '--color-dark-50': colors.dark[50],
  '--color-dark-100': colors.dark[100],
  '--color-dark-200': colors.dark[200],
  '--color-dark-300': colors.dark[300],
  '--color-dark-400': colors.dark[400],
  '--color-dark-500': colors.dark[500],
  '--color-dark-base': colors.dark.base,
  '--color-dark-deep': colors.dark.deep,
  '--color-dark-accent': colors.dark.accent,

  // ── Amber ─────────────────────────────────────────────────────
  '--color-amber-50': colors.amber[50],
  '--color-amber-100': colors.amber[100],
  '--color-amber-200': colors.amber[200],
  '--color-amber-300': colors.amber[300],
  '--color-amber-400': colors.amber[400],
  '--color-amber-500': colors.amber[500],
  '--color-amber-600': colors.amber[600],
  '--color-amber-700': colors.amber[700],
  '--color-amber-800': colors.amber[800],
  '--color-amber-900': colors.amber[900],

  // ── Neutral ───────────────────────────────────────────────────
  '--color-neutral-50': colors.neutral[50],
  '--color-neutral-100': colors.neutral[100],
  '--color-neutral-200': colors.neutral[200],
  '--color-neutral-300': colors.neutral[300],
  '--color-neutral-400': colors.neutral[400],
  '--color-neutral-500': colors.neutral[500],
  '--color-neutral-600': colors.neutral[600],
  '--color-neutral-700': colors.neutral[700],
  '--color-neutral-800': colors.neutral[800],
  '--color-neutral-900': colors.neutral[900],

  // ── Semantic ──────────────────────────────────────────────────
  '--color-success': colors.success,
  '--color-warning': colors.warning,
  '--color-error': colors.error,
  '--color-info': colors.info,

  // ── UI roles ──────────────────────────────────────────────────
  '--color-background': colors.background,
  '--color-background-deep': colors.backgroundDeep,
  '--color-surface': colors.surface,
  '--color-surface-elevated': colors.surfaceElevated,
  '--color-border': colors.border,
  '--color-border-hover': colors.borderHover,
  '--color-border-subtle': colors.borderSubtle,

  // Full background gradient (use on html / root page containers)
  '--bg-gradient':
    'linear-gradient(180deg, #010204 0%, #000101 100%),' +
    'radial-gradient(36.46% 41.67% at 100% 100%, rgba(0, 19, 64, 0.25) 0%, rgba(0, 19, 64, 0) 60%)',

  // ── Text ──────────────────────────────────────────────────────
  '--color-text-primary': colors.text.primary,
  '--color-text-secondary': colors.text.secondary,
  '--color-text-muted': colors.text.muted,
  '--color-text-disabled': colors.text.disabled,
  '--color-text-inverse': colors.text.inverse,

  // ── Semantic shorthand aliases (used in global.css classes) ───
  '--color-teal': colors.primary[500],
  '--color-teal-light': colors.primary[400],
  '--color-teal-dark': colors.primary[700],
  '--color-teal-glow': colors.glow.tealLg,
  '--color-amber': colors.amber[500],
  '--color-amber-light': colors.amber[400],
  '--color-amber-dark': colors.amber[700],
  '--color-amber-glow': colors.glow.amberLg,
  '--color-white': '#f0f8ff',
  '--color-muted': colors.text.muted,
  '--color-dim': colors.text.disabled,

  // Legacy alias (kept so existing CSS classes using --color-navy still resolve)
  '--color-navy': colors.background,
  '--color-navy-deep': colors.backgroundDeep,

  // Grid
  '--grid-color': 'rgba(0, 207, 207, 0.06)',
  '--grid-size': '40px',

  // ── Buttons ───────────────────────────────────────────────────
  '--color-button-primary-bg': colors.button.primary.bg,
  '--color-button-primary-border': colors.button.primary.border,
  '--color-button-primary-text': colors.button.primary.text,
  '--color-button-primary-hover-bg': colors.button.primary.hoverBg,
  '--color-button-primary-hover-border': colors.button.primary.hoverBorder,
  '--color-button-primary-glow': colors.button.primary.glow,
  '--color-button-secondary-bg': colors.button.secondary.bg,
  '--color-button-secondary-border': colors.button.secondary.border,
  '--color-button-secondary-text': colors.button.secondary.text,
  '--color-button-secondary-hover-bg': colors.button.secondary.hoverBg,
  '--color-button-ghost-text': colors.button.ghost.text,
  '--color-button-ghost-hover-text': colors.button.ghost.hoverText,
  '--color-button-danger-bg': colors.button.danger.bg,
  '--color-button-danger-border': colors.button.danger.border,
  '--color-button-danger-text': colors.button.danger.text,
  '--color-button-danger-hover-bg': colors.button.danger.hoverBg,
  '--color-button-danger-hover-border': colors.button.danger.hoverBorder,
  '--color-button-glass-bg': colors.button.glass.bg,
  '--color-button-glass-border': colors.button.glass.border,
  '--color-button-glass-text': colors.button.glass.text,
  '--color-button-glass-hover-bg': colors.button.glass.hoverBg,
  '--color-button-glass-hover-border': colors.button.glass.hoverBorder,
  '--color-button-export-bg': colors.button.export.bg,
  '--color-button-export-text': colors.button.export.text,

  // ── Auth & Inputs ─────────────────────────────────────────────
  '--color-auth-heading': colors.auth.heading,
  '--color-auth-subheading': colors.auth.subheading,
  '--color-auth-link': colors.auth.link,
  '--color-auth-link-hover': colors.auth.linkHover,
  '--color-auth-step-active-bg': colors.auth.stepActiveBg,
  '--color-auth-step-active-border': colors.auth.stepActiveBorder,
  '--color-auth-step-active-glow': colors.auth.stepActiveGlow,
  '--color-auth-step-active-text': colors.auth.stepActiveText,
  '--color-auth-step-inactive-bg': colors.auth.stepInactiveBg,
  '--color-auth-step-inactive-border': colors.auth.stepInactiveBorder,
  '--color-auth-step-inactive-text': colors.auth.stepInactiveText,
  '--color-auth-input-bg': colors.auth.inputBg,
  '--color-auth-input-border': colors.auth.inputBorder,
  '--color-auth-input-label': colors.auth.inputLabel,

  // ── Status & Accents ──────────────────────────────────────────
  '--color-status-warning': colors.status.warning,
  '--color-status-warning-text': colors.status.warningText,
  '--color-status-sky': colors.status.sky,

  // ── Chart & Brand ─────────────────────────────────────────────
  '--color-chart-green': colors.chart.green,
  '--color-chart-green-dark': colors.chart.greenDark,
  '--color-brand-zoom': colors.brand.zoom,
  '--color-brand-meet': colors.brand.meet,
  '--color-brand-meet-sec': colors.brand.meetSecondary,

  // ── Glass & Dropdowns ─────────────────────────────────────────
  '--color-glass-surface': colors.glass.surface,
  '--color-glass-subtle': colors.glass.subtle,
  '--color-glass-skeleton': colors.glass.skeleton,
  '--color-dropdown-bg': colors.dropdown.bg,
  '--color-dropdown-glass': colors.dropdown.glassBg,

  // ── Layout ────────────────────────────────────────────────────
  '--nav-height': layout.navHeight,
  '--max-width': layout.maxWidth,
  '--section-pad-y': layout.sectionPaddingY,
};
